import React from 'react';

import { gql } from '@apollo/client';
import { cloneDeep } from 'lodash';

import {
  AttributeNameType,
  AttributesFilterFragment,
  useEmployeeAttributeNamesQuery,
} from 'src/generated/mloop-graphql';

import { BaseAttributesFilter } from 'src/entities/InterviewPlan/InterviewSeat/ModuleMemberFilters';

import { AttributeMap, JobStageInterviewSeat } from 'src/store/slices/job-stage-interview-seat';

export { BaseAttributesFilter } from 'src/entities/InterviewPlan/InterviewSeat/ModuleMemberFilters';

type AttributesFilterProps = {
  seat: JobStageInterviewSeat;
  filterText: string;
  onNoAttributes: () => void;
  onUpdated: (seat: JobStageInterviewSeat) => void;
};

const AttributesFilter = ({ seat, filterText, onNoAttributes, onUpdated }: AttributesFilterProps): JSX.Element => {
  const {
    loading: attributesLoading,
    error: attributesError,
    data: attributesData,
  } = useEmployeeAttributeNamesQuery({
    variables: {
      input: {
        types: Object.values(AttributeNameType), // fetching all attribute types
      },
    },
    fetchPolicy: 'network-only', // force this to fetch from the backend each time in case attributes are added while in the scheduling flow
  });

  const handleAttributeUpdate = (attributeNameId: string, attributeValueId, selected: boolean) => {
    const updatedSeat = cloneDeep(seat);
    const attributeMap: AttributeMap = {};

    // This deepCopy is needed because the attributes we get from backend are immutable
    Object.keys(seat.attributeMap ?? {}).forEach((key) => {
      if (seat.attributeMap) {
        attributeMap[key] = [...(seat.attributeMap[key] || [])];
      }
    });

    if (selected) {
      if (!attributeMap[attributeNameId]) {
        attributeMap[attributeNameId] = [];
      }
      attributeMap[attributeNameId].push(attributeValueId);
    } else {
      attributeMap[attributeNameId] = attributeMap[attributeNameId].filter((id) => id !== attributeValueId);

      if (attributeMap[attributeNameId].length === 0) {
        delete attributeMap[attributeNameId];
      }
    }
    updatedSeat.attributeMap = attributeMap;
    delete updatedSeat.selectedTrainedInterviewerIds;
    delete updatedSeat.selectedShadowInterviewerIds;
    delete updatedSeat.selectedReverseShadowInterviewerIds;
    onUpdated(updatedSeat);
  };

  if (
    attributesData &&
    (!attributesData.employeeAttributeNames?.items || attributesData.employeeAttributeNames.items.length === 0)
  ) {
    onNoAttributes();
  }

  return (
    <BaseAttributesFilter
      loading={attributesLoading && !attributesData}
      filterText={filterText}
      error={attributesError}
      data={attributesData?.employeeAttributeNames?.items as AttributesFilterFragment[]}
      attributeMap={seat.attributeMap}
      onAttributeUpdate={handleAttributeUpdate}
    />
  );
};

AttributesFilter.fragments = {
  employeeAttributeNames: gql`
    fragment AttributesFilter on EmployeeAttributeName {
      id
      name
      type
      values {
        id
        value
      }
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EmployeeAttributeNamesQuery = gql`
  ${AttributesFilter.fragments.employeeAttributeNames}
  query employeeAttributeNames($input: EmployeeAttributeNamesInput!) {
    employeeAttributeNames(input: $input) {
      items {
        ...AttributesFilter
      }
    }
  }
`;

export default AttributesFilter;
